"use client";

import React from "react";
import Reveal from "@/components/common/Reveal";
import { SlidersHorizontal, ShieldCheck, ArrowRight, FileText, CircleCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
export default function MaterialsHero() {
  const {
    lang
  } = useLanguage();
  const copy = {
    categoryBadge: {
      en: "Materials & Surface Finishes",
      zh: "材料牌号与表面处理",
      es: "Materiales y acabados superficiales",
      de: "Werkstoffe & Oberflächenbehandlungen",
      fr: "Matériaux et finitions de surface",
      pt: "Materiais e acabamentos de superfície"
    },
    h1: {
      en: "Stainless Steel Alloy Selection & Surface Finish Guide for Filtration Mesh",
      zh: "过滤丝网不锈钢合金选型与表面处理规格指南",
      es: "Guía de selección de aleaciones de acero inoxidable y acabados para malla filtrante",
      de: "Leitfaden zur Auswahl von Edelstahllegierungen und Oberflächen für Filtergewebe",
      fr: "Guide de sélection des alliages d'acier inoxydable et finitions pour toiles filtrantes",
      pt: "Guia de seleção de ligas de aço inoxidável e acabamentos para malha de filtração"
    },
    lead: {
      en: "Select the optimal alloy composition and surface condition engineered for specific chemical exposure, thermal threshold, and mechanical loads. MutualFilter manufactures technical woven wire mesh and sintered porous media in standard austenitic grades and high-performance alloys.",
      zh: "针对化工介质、高温工况及高要求过滤环境，精选适宜的金属合金成分与表面状态。MutualFilter 生产全系列标准奥氏体不锈钢及高性能合金精密金属编织网与多层真空烧结滤材。",
      es: "Seleccione la composición de aleación y el estado superficial óptimos para exposiciones químicas específicas, umbrales térmicos y cargas mecánicas. MutualFilter fabrica mallas de alambre tejidas y medios sinterizados en grados austeníticos estándar y aleaciones de alto rendimiento.",
      de: "Wählen Sie die optimale Legierungszusammensetzung und Oberflächenbeschaffenheit für spezifische chemische Belastungen, Temperaturgrenzen und mechanische Anforderungen. MutualFilter fertigt technisches Drahtgewebe und Sintermedien in austenitischen Standardgüten und Hochleistungslegierungen.",
      fr: "Sélectionnez la composition d'alliage et l'état de surface optimaux pour vos contraintes chimiques, thermiques et mécaniques. MutualFilter produit des toiles métalliques tissées et des médias frittés en inox austénitique standard et alliages haute performance.",
      pt: "Selecione a composição de liga e a condição superficial ideais para exposição química específica, limites térmicos e cargas mecânicas. A MutualFilter fabrica malhas de arame tecidas e meios sinterizados em graus austeníticos padrão e ligas de alto desempenho."
    },
    secondaryCta: {
      en: "Material Verification",
      zh: "材料化验与验证规范",
      es: "Verificación de materiales",
      de: "Materialprüfung & Nachweis",
      fr: "Vérification des matériaux",
      pt: "Verificação de materiais"
    },
    overlayChip: {
      en: "Austenitic Grain Structure (ASTM E112)",
      zh: "奥氏体金相组织 (ASTM E112)",
      es: "Estructura de grano austenítico (ASTM E112)",
      de: "Austenitische Kornstruktur (ASTM E112)",
      fr: "Structure de grain austénitique (ASTM E112)",
      pt: "Estrutura de grão austenítico (ASTM E112)"
    },
    cardFootNote: {
      en: "Mill Test Reports & PMI verification available upon order",
      zh: "钢厂化验报告 (MTR) 与 PMI 正材分析可按订单提供",
      es: "Informes de fábrica (MTR) y verificación PMI según pedido",
      de: "Werksprüfzeugnisse (MTR) und PMI-Prüfung auf Bestellung",
      fr: "Certificats usine (MTR) et contrôle PMI disponibles sur commande",
      pt: "Relatórios de usina (MTR) e testes PMI disponíveis sob pedido"
    },
    cardFootLink: {
      en: "Alloy Matrix",
      zh: "合金矩阵",
      es: "Matriz de aleaciones",
      de: "Legierungsmatrix",
      fr: "Matrice d'alliages",
      pt: "Matriz de ligas"
    },
    imageAlt: {
      en: "Cold-toned stainless steel wire mesh etched grain microstructure under metallurgical microscope",
      zh: "金相显微镜下经过化学腐蚀的冷轧不锈钢丝网金相显微晶粒组织",
      es: "Microestructura de grano grabado de malla de acero inoxidable bajo microscopio metalúrgico",
      de: "Geätztes Gefügebild von Edelstahldrahtgewebe unter dem Metallurgiemikroskop",
      fr: "Microstructure des grains d'une toile en acier inoxydable après attaque chimique sous microscope métallurgique",
      pt: "Microestrutura de grãos atacados de malha de aço inoxidável sob microscópio metalúrgico"
    }
  };
  const technicalHighlights = [{
    label: pick(lang, {
      en: "Alloy Grades",
      zh: "常规合金牌号",
      es: "Grados de aleación",
      de: "Legierungsgüten",
      fr: "Nuances d'alliages",
      pt: "Graus de ligas"
    }),
    val: "AISI 304, 304L, 316, 316L, 310S"
  }, {
    label: pick(lang, {
      en: "Woven Range",
      zh: "编织精度范围",
      es: "Rango de tejido",
      de: "Gewebefeinheit",
      fr: "Plage de tissage",
      pt: "Faixa de tecelagem"
    }),
    val: "1 µm – 25 mm (1 – 635 Mesh)"
  }, {
    label: pick(lang, {
      en: "Surface Treatments",
      zh: "主要表面工艺",
      es: "Tratamientos superficiales",
      de: "Oberflächenbehandlungen",
      fr: "Traitements de surface",
      pt: "Tratamentos superficiais"
    }),
    val: "ASTM A967 Passivation, Degreasing"
  }, {
    label: pick(lang, {
      en: "Manufacturing Standards",
      zh: "制造执行标准",
      es: "Normas de fabricación",
      de: "Fertigungsnormen",
      fr: "Normes de fabrication",
      pt: "Normas de fabricação"
    }),
    val: "ISO 9044 / ASTM E2016"
  }];
  return <section data-nav="light" className="relative overflow-hidden bg-background mf-hero border-b border-border text-foreground">
      <div className="mf-container">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
          {/* Left Column (7 cols on lg) */}
          <Reveal className="lg:col-span-7 flex flex-col items-start">
            {/* Badges row: exactly two outline badges */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-card px-3 py-1 font-mono mf-caption text-foreground">
                <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                <span>{pick(lang, copy.categoryBadge)}</span>
              </Badge>
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-muted px-3 py-1 font-mono mf-caption text-foreground">
                <ShieldCheck className="size-3.5 text-primary" aria-hidden="true" />
                <span>ISO 9044 / ASTM E2016</span>
              </Badge>
            </div>

            {/* Single H1 */}
            <h1 className="font-default text-balance mf-h1 text-foreground">
              {pick(lang, copy.h1)}
            </h1>

            {/* Lead paragraph */}
            <p className="mt-4 max-w-2xl text-muted-foreground mf-lead">
              {pick(lang, copy.lead)}
            </p>

            {/* CTAs */}
            <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button size="lg" asChild>
                <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                  <span>{pick(lang, COMMON.requestQuote)}</span>
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </Button>

              <Button variant="outline" size="lg" asChild>
                <Link to="/StainlessSteelMaterialVerificationPage" className="inline-flex items-center gap-2">
                  <FileText className="size-4" aria-hidden="true" />
                  <span>{pick(lang, copy.secondaryCta)}</span>
                </Link>
              </Button>
            </div>
          </Reveal>

          {/* Right Column: Spec card with macro photo (5 cols on lg) */}
          <Reveal delay={60} className="lg:col-span-5 w-full">
            <Card className="overflow-hidden rounded-lg border border-border bg-card gap-0 py-0">
              <div className="mf-card-media relative mf-ratio-hero w-full">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-brushed-stainl-57b077.png" alt={pick(lang, copy.imageAlt)} loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover object-center" />
                <div className="absolute top-3 left-3 bg-card border border-border px-3 py-1 rounded-md mf-caption font-mono flex items-center gap-1.5 text-foreground">
                  <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                  <span>{pick(lang, copy.overlayChip)}</span>
                </div>
              </div>

              {/* Single footer row */}
              <CardContent className="p-4">
                <div className="flex items-center justify-between mf-caption">
                  <div className="flex items-center gap-1.5 text-muted-foreground min-w-0">
                    <CircleCheck className="size-3.5 text-primary shrink-0" aria-hidden="true" />
                    <span className="truncate">{pick(lang, copy.cardFootNote)}</span>
                  </div>
                  <Link to="/CorrosionResistanceMatricesPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0">
                    <span>{pick(lang, copy.cardFootLink)}</span>
                    <ArrowRight className="size-3.5" aria-hidden="true" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </Reveal>
        </div>

        {/* Spec strip under grid */}
        <div className="mf-spec-strip">
          {technicalHighlights.map((spec, idx) => <div key={idx} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">{spec.label}</span>
              <span className="mf-spec-strip__value">{spec.val}</span>
            </div>)}
        </div>
      </div>
    </section>;
}